/**
 * The History screen's pure logic — no React, no fetching, so it can be tested
 * directly. Same split as `format.js` and `utils/matchplay.js`.
 *
 * NO MONEY IS COMPUTED HERE. Every figure arrives from the server, which folds
 * it once out of the `BetDebt` rows. This file sorts, filters, captions and
 * scales bars. The moment it starts adding stakes up there are two ledgers.
 *
 * COPY RULE: up / down / level, never owes, owed, pays or settle. Directional
 * phrasing is always from the viewer's side.
 */

/** Positive means you are UP by that much. */
export const netOf = (payload, playerId) => payload?.nets?.[playerId] ?? 0;

/**
 * What stands between you and them. POSITIVE means YOU are up on them.
 *
 * Read off `positions`, which names an `up` and a `down` player, so the sign
 * cannot be misread the way a bare balance can.
 */
export const vsOf = (payload, myPlayerId, playerId) => {
  const found = (payload?.positions || []).find(
    (p) => (p.up === myPlayerId && p.down === playerId)
      || (p.up === playerId && p.down === myPlayerId));
  if (!found) return 0;
  return found.up === myPlayerId ? found.amount : -found.amount;
};

/** Your own positions, biggest first — the YOU V THE SOCIETY list. */
export const myPositions = (payload, myPlayerId) =>
  (payload?.positions || [])
    .filter((p) => p.up === myPlayerId || p.down === myPlayerId)
    .map((p) => ({
      playerId: p.up === myPlayerId ? p.down : p.up,
      // Positive: you are up on them.
      amount: p.up === myPlayerId ? p.amount : -p.amount,
    }))
    .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));

/** Everyone else's positions — the ACROSS THE SOCIETY list. */
export const societyPositions = (payload, myPlayerId) =>
  (payload?.positions || [])
    .filter((p) => p.up !== myPlayerId && p.down !== myPlayerId);

/** Up on / down to / net, for the season standing card. */
export const standing = (payload, myPlayerId) => {
  let upOn = 0;
  let downTo = 0;
  myPositions(payload, myPlayerId).forEach((p) => {
    if (p.amount > 0) upOn += p.amount;
    else downTo += -p.amount;
  });
  return {
    net: Math.round((upOn - downTo) * 100) / 100,
    upOn: Math.round(upOn * 100) / 100,
    downTo: Math.round(downTo * 100) / 100,
  };
};

/**
 * The trend bars: your running position over the last `limit` rounds, oldest
 * first.
 *
 * A RUNNING TOTAL, not per-round deltas — the question is "where have I got
 * to", and a row of independent ups and downs does not answer it. Heights are
 * scaled to the biggest absolute position in the window so the shape is
 * readable whatever the stakes.
 */
export const trend = (payload, limit = 8) => {
  const rounds = [...(payload?.rounds || [])].reverse();   // oldest first
  let running = 0;
  const points = rounds.map((round) => {
    running = Math.round((running + (round.my_delta || 0)) * 100) / 100;
    return { key: round.key, name: round.course_name || round.name,
             delta: round.my_delta || 0, cumulative: running };
  });
  const window = points.slice(-limit);
  const peak = window.reduce(
    (max, p) => Math.max(max, Math.abs(p.cumulative)), 0);
  return window.map((p) => ({
    ...p,
    // 4px floor so a level round is still a mark rather than a gap.
    height: peak === 0 ? 4 : Math.max(4, Math.round(
      (Math.abs(p.cumulative) / peak) * 28)),
    up: p.cumulative >= 0,
  }));
};

/** "Last 8 rounds · down £10 at Worplesdon" */
export const trendCaption = (points, money) => {
  if (!points.length) return 'No rounds yet';
  const last = points[points.length - 1];
  const where = last.name || 'the last round';
  const label = last.delta > 0 ? `up ${money(last.delta)}`
    : last.delta < 0 ? `down ${money(-last.delta)}`
      : 'level';
  return `Last ${points.length} ${
    points.length === 1 ? 'round' : 'rounds'} · ${label} at ${where}`;
};

/** "1 round with you", never "1 rounds" — one helper, as the handoff asks. */
export const roundsLabel = (n) =>
  `${n} ${n === 1 ? 'round' : 'rounds'} with you`;

/** The subline under a player in STANDING BY PLAYER. */
export const playerSubline = (payload, myPlayerId, playerId, money) => {
  const rounds = payload?.rounds_with?.[playerId] || 0;
  if (!rounds) return 'No bets with you yet';
  const vs = vsOf(payload, myPlayerId, playerId);
  if (vs === 0) return `${roundsLabel(rounds)} · level`;
  return `${roundsLabel(rounds)} · you are ${
    vs > 0 ? 'up' : 'down'} ${money(Math.abs(vs))}`;
};

export const SORTS = [
  { value: 'net', label: 'Net' },
  { value: 'vs', label: 'V you' },
  { value: 'name', label: 'A–Z' },
];

/** The STANDING BY PLAYER list, sorted and filtered. */
export const standingRows = (payload, myPlayerId, { sort, query } = {}) => {
  const rows = (payload?.players || [])
    .filter((p) => p.player_id !== myPlayerId)
    .map((p) => ({
      playerId: p.player_id,
      name: p.name,
      net: netOf(payload, p.player_id),
      vs: vsOf(payload, myPlayerId, p.player_id),
      rounds: payload?.rounds_with?.[p.player_id] || 0,
    }));

  const needle = (query || '').trim().toLowerCase();
  const filtered = needle
    ? rows.filter((r) => r.name.toLowerCase().includes(needle))
    : rows;

  const sorted = [...filtered];
  if (sort === 'name') {
    sorted.sort((a, b) => a.name.localeCompare(b.name));
  } else if (sort === 'vs') {
    sorted.sort((a, b) => Math.abs(b.vs) - Math.abs(a.vs)
      || a.name.localeCompare(b.name));
  } else {
    sorted.sort((a, b) => b.net - a.net || a.name.localeCompare(b.name));
  }
  return sorted;
};

/**
 * Width of a diverging bar, as a percentage of the half-track.
 *
 * Scaled to the biggest absolute net on screen, with a floor so a small
 * position is still visible rather than a hairline.
 */
export const barWidth = (value, maxAbs) => {
  if (!maxAbs) return 0;
  return Math.max(1.5, (Math.abs(value) / maxAbs) * 50);
};

/** "Simon & Jonny v Nick B & Will", or "Sweep · 8 players". */
export const betTitle = (bet, shortName) => {
  if (bet.kind === 'sweep') {
    return `Sweep · ${bet.entrants} players`;
  }
  return (bet.sides || [])
    .map((side) => side.map((p) => shortName(p.name)).join(' & '))
    .join(' v ');
};

const ORDINALS = ['1st', '2nd', '3rd', '4th', '5th', '6th'];

/**
 * Who collected, said correctly.
 *
 * A SWEEP PAYS PLACES. "won by Simon & Matt" over a top-two sweep reads as a
 * dead heat when Simon was first and Matt second — which is what it said on a
 * real round. Places are not stored, but the shares descend by place and a tie
 * SHARES the place money, so equal amounts are a genuinely shared place and
 * anything else is a rank.
 *
 * Head to head and pairs keep "won by": there is one winning side, and a pair
 * did win it together.
 */
export const winnerLine = (bet, shortName) => {
  const winners = bet.won_by || [];
  if (!winners.length) return null;
  // Older payloads sent bare names; a cached one must not crash the screen.
  if (typeof winners[0] === 'string') {
    return `won by ${winners.map(shortName).join(' & ')}`;
  }
  const named = (list) => list.map((w) => shortName(w.name)).join(' & ');
  if (bet.kind !== 'sweep' || winners.length === 1) {
    return `won by ${named(winners)}`;
  }
  const shares = bet.splits || [];
  const paysTheSame = (rank) => shares.length > rank
    && shares[rank - 1] === shares[rank];

  const groups = [];
  winners.forEach((winner, index) => {
    const last = groups[groups.length - 1];
    const tied = last && last[0].amount === winner.amount
      && !paysTheSame(index);
    if (tied) last.push(winner);
    else groups.push([winner]);
  });
  let place = 1;
  return groups.map((group) => {
    const label = ORDINALS[place - 1] || `${place}th`;
    place += group.length;
    return `${label} ${named(group)}`;
  }).join(' · ');
};

/** "Pairs · nett · £30 · won by Nick B & Will" */
export const betSubline = (bet, money, shortName) => {
  const kind = { h2h: 'Head to head', pairs: 'Pairs', sweep: 'Sweep' }[bet.kind]
    || bet.kind;
  const parts = [kind, bet.format, money(bet.stake)];
  if (bet.status === 'open') {
    parts.push('not settled yet');
  } else if (bet.status === 'void') {
    parts.push('void — no card');
  } else if (!bet.won_by?.length) {
    parts.push('halved');
  } else {
    parts.push(winnerLine(bet, shortName));
  }
  return parts.join(' · ');
};

/** Rounds for Tab B, honouring the All / Just yours filter. */
export const filteredRounds = (payload, filter) => {
  const rounds = payload?.rounds || [];
  if (filter !== 'mine') return rounds;
  return rounds
    .map((round) => ({
      ...round,
      bets: round.bets.filter((b) => b.mine),
    }))
    .filter((round) => round.bets.length > 0);
};

/** "8 rounds · 25 bets · 12 yours" */
export const roundsSummary = (payload) => {
  const rounds = payload?.rounds || [];
  const bets = rounds.reduce((n, r) => n + r.bets.length, 0);
  const mine = rounds.reduce(
    (n, r) => n + r.bets.filter((b) => b.mine).length, 0);
  return `${rounds.length} ${rounds.length === 1 ? 'round' : 'rounds'} · ${
    bets} ${bets === 1 ? 'bet' : 'bets'} · ${mine} yours`;
};

/**
 * Which round of which competition this card is.
 *
 * Three cards reading "Drift Golf Club · 2026-09-21 · Stableford" are three
 * different rounds nobody can tell apart — the course, the date and the format
 * are the things they have in COMMON. What separates them is the round number,
 * and whether the bet was on one day or on the competition as a whole.
 */
export const roundScope = (round) => {
  if (!round) return '';
  if (round.round_number == null) {
    const count = round.round_count || 0;
    return count > 1 ? `All ${count} rounds` : 'Whole competition';
  }
  const count = round.round_count || 0;
  return count > 1 ? `Round ${round.round_number} of ${count}`
    : 'Single round';
};

/**
 * The figure on a round card.
 *
 * IT IS YOUR POSITION, NOT THE ROUND'S RESULT. A round can hold several bets
 * that finished differently — one halved, one won by somebody else — so there
 * is no single result to report at this level, and the card never tries to
 * give one. What it can say is what the round did to YOU.
 *
 * WHICH IS NOTHING WHEN NONE OF THEM ARE YOURS. `my_delta` is 0 both when you
 * came out level and when you were not in a single bet, and printing "Level"
 * for the second reads as a verdict on the golf — it sat over a round whose
 * pairs bet had a clear winner. A dash, the same mark the bet rows already
 * use for somebody else's bet, says "nothing of yours here".
 */
export const roundPill = (round, myBets, money) => {
  if (!myBets) {
    return { text: '—', tone: ' is-none', title: 'None of these are yours' };
  }
  const delta = round.my_delta || 0;
  if (delta === 0) {
    return { text: 'Level', tone: '', title: 'Your bets on this round cancel out' };
  }
  return {
    text: `${delta > 0 ? '+' : '\u2212'}${money(Math.abs(delta))}`,
    tone: delta > 0 ? ' is-up' : ' is-down',
    title: delta > 0 ? 'You are up on this round' : 'You are down on this round',
  };
};
